/**
 * lib/env/useEnvironment.tsx — Provider de ENTORNO + CAPABILITY + BLOQUEO.
 *
 * La sonda corre UNA vez por sesión (sin tormenta de peticiones en StrictMode, gracias a
 * la deduplicación por promesa en vuelo) y se comparte por contexto. Si la identidad real
 * del servidor no coincide con el entorno solicitado, se BLOQUEA toda la app.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { supabase } from '@lib/supabase/client'
import {
  detectEnvironment,
  probeRuntime,
  resolveBlock,
  type EnvInfo,
  type AnalyticsCapability,
} from './environment'
import { EnvContext } from './envContext'
import { marcarIdentidadVerificada, registrarSondaDeReconexion } from '@lib/net/reachability'

// Caché de la sonda a NIVEL MÓDULO: StrictMode monta el provider dos veces (y desmonta el
// primero); un useRef no dedup entre ambos montajes, pero esta promesa compartida sí.
let sharedProbe: Promise<AnalyticsCapability> | null = null
// Identidad ya sondeada (undefined = nunca). La identidad/capability del backend es estable por
// sesión: sólo cambia con login/logout/otro usuario. Evita re-sondear (y repetir el 404 en
// principal 00210) ante TOKEN_REFRESHED o SIGNED_IN/INITIAL_SESSION repetidos del MISMO usuario.
let probedUid: string | null | undefined = undefined

export function EnvironmentProvider({ children }: { children: ReactNode }) {
  const [env] = useState<EnvInfo>(() => detectEnvironment())
  const [analytics, setAnalytics] = useState<AnalyticsCapability>({ status: 'loading' })
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let alive = true
    setAnalytics({ status: 'loading' })
    // Reutiliza la promesa compartida (dedup real entre montajes de StrictMode).
    const p = sharedProbe ?? probeRuntime()
    sharedProbe = p
    void p.then(cap => {
      if (alive) setAnalytics(cap)
      // Libera la caché sólo cuando ESTA promesa termina (permite re-sondar con refresh).
      if (sharedProbe === p) sharedProbe = null
    })
    return () => {
      alive = false
    }
  }, [tick])

  // La capability depende de la IDENTIDAD, no de cada evento de auth. Se re-sondea SÓLO cuando
  // el usuario cambia (login/logout/otro usuario); los SIGNED_IN/INITIAL_SESSION repetidos y los
  // TOKEN_REFRESHED del mismo usuario NO alteran la identidad del backend → no re-sondean.
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      const uid = session?.user?.id ?? null
      if (uid !== probedUid) {
        probedUid = uid
        sharedProbe = null
        setTick(t => t + 1)
      }
    })
    return () => data.subscription.unsubscribe()
  }, [])

  // Una tienda que recupera la conexión debe volver a operar en línea sin que nadie
  // recargue nada. El módulo de alcance lleva la vigilancia; aquí sólo se le presta la
  // forma de repreguntar. Sirve igual si la caída la detectó un cobro fallido.
  useEffect(() => {
    registrarSondaDeReconexion(() => setTick(t => t + 1))
  }, [])

  const bloqueo = resolveBlock(env, analytics)

  // La cola offline no se vacía contra cualquier servidor que responda: exige identidad
  // verificada y sin bloqueo. Estar sin conexión no es una autorización.
  useEffect(() => {
    marcarIdentidadVerificada(
      bloqueo === null && analytics.status !== 'loading' && analytics.status !== 'offline'
    )
  }, [bloqueo, analytics.status])

  const value = useMemo(
    () => ({
      env,
      analytics,
      block: bloqueo,
      refreshCapability: () => {
        probedUid = undefined // fuerza un re-sondeo real (errores de red / reintento manual)
        sharedProbe = null
        setTick(t => t + 1)
      },
    }),
    [env, analytics, bloqueo]
  )

  return <EnvContext.Provider value={value}>{children}</EnvContext.Provider>
}
